import styled, { css, keyframes } from 'styled-components';

import { Text } from '@/nova/components';
import { hoverWash } from '@/nova/theme/mixins';
import { CUST, CUST_ANIM, CUST_GRADIENTS, CUST_REDUCED_MOTION } from '../../../../theme/customizer-tokens';

type Phase = 'review' | 'deploying' | 'done';

/* Glossy highlight sweeping across the live progress fill (background-position
 * slide, same technique as the configurator-v3 pack-finder scan sheen). */
const fillSheen = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

/* ── Desktop shell (step 4 owns the whole inner layout) ──────────────────── */

export const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Avatar = styled.div`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  background: ${CUST.headerAvatarBg};
  color: ${CUST.white};
`;

/** Review-phase leading affordance: a back button that takes the place of the
 *  status Avatar so the owner can return to the previous step and change details
 *  before going live. Only rendered while `review`; once deploying/done it is
 *  gone (deploy is a one-way door). Same circular plate as the Avatar, now an
 *  interactive control with a hover tint. */
export const BackButton = styled.button`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: ${CUST.headerAvatarBg};
  color: ${CUST.white};
  cursor: pointer;
  transition: background ${CUST_ANIM.INTERACT_MS}ms ease;

  &:hover {
    background: ${CUST.headerAvatarBgHover};
  }
`;

export const HeaderTitle = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  color: ${CUST.white};
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const LiveRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const LiveDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 9999px;
  background: ${CUST.liveDot};
`;

export const LiveText = styled(Text)`
  font-size: 12px;
  color: ${CUST.textMuted};
`;

export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  gap: 48px;
`;

/** The left preview container ("Sidebar - Finish"): a rounded gradient panel with
 *  a hairline border that frames the devices and clips the MacBook's off-canvas bleed. */
export const PreviewCol = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  overflow: hidden;
  border: 1px solid ${CUST.panelBorder};
  border-radius: 20px;
  background-image: ${CUST_GRADIENTS.previewPanel};
`;

/** Small muted state label floated over the preview ("Ready to apply…"). */
export const PreviewLabel = styled(Text)`
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 5;
  font-size: 16px;
  font-weight: 300;
  text-align: right;
  opacity: 0.8;
  color: ${CUST.white};
`;

/* ── Right sidebar ───────────────────────────────────────────────────────── */

export const Sidebar = styled.aside`
  flex: 0 0 320px;
  width: 320px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${CUST.panelBorder};
  border-radius: 20px;
  background-image: ${CUST_GRADIENTS.deploySidebar};
  overflow: hidden;
`;

export const SideHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 28px 16px 16px;
`;

export const SideHeaderRow = styled.div<{ $center?: boolean }>`
  display: flex;
  align-items: flex-end;
  justify-content: ${({ $center }) => ($center ? 'center' : 'space-between')};
  gap: 8px;
  width: 100%;
`;

export const RecordDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: ${CUST.lumioBorder};
`;

const labelTone = (variant: Phase) => {
  if (variant === 'review') {
    return css`
      background-image: ${CUST_GRADIENTS.headingGradient};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    `;
  }
  if (variant === 'deploying') {
    return css`
      background-image: ${CUST_GRADIENTS.deployProgressText};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    `;
  }
  return css`
    color: ${CUST.deployCompletedLabel};
  `;
};

export const LabelGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const SideHeaderLabel = styled(Text)<{ $variant: Phase }>`
  font-size: ${({ $variant }) => ($variant === 'review' ? '16px' : '12px')};
  font-weight: 500;
  ${({ $variant }) => labelTone($variant)}
`;

export const Pct = styled(Text)`
  font-size: 11px;
  color: ${CUST.textMuted};
`;

export const Track = styled.div<{ $variant: Phase }>`
  width: 100%;
  height: 6px;
  border-radius: 68px;
  overflow: hidden;
  background: ${({ $variant }) => ($variant === 'review' ? CUST.deployTrackReview : CUST.deployProgressTrack)};
`;

export const Fill = styled.div<{ $pct: number; $done: boolean }>`
  position: relative;
  overflow: hidden;
  height: 100%;
  width: ${({ $pct }) => `${Math.max(0, Math.min(100, $pct))}%`};
  border-radius: 160px;
  background: ${({ $done }) => ($done ? CUST.deployGreen : 'transparent')};
  background-image: ${({ $done }) => ($done ? 'none' : CUST_GRADIENTS.deployProgress)};
  transition: width 400ms ease;

  /* A soft sheen glides across the magenta→cyan fill while deploying, then
     disappears once the bar settles to solid green (done). */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    display: ${({ $done }) => ($done ? 'none' : 'block')};
    background: ${CUST_GRADIENTS.deploySheen};
    background-size: 200% 100%;
    background-repeat: no-repeat;
    animation: ${fillSheen} ${CUST_ANIM.DEPLOY_SHEEN_MS}ms linear infinite;
  }

  ${CUST_REDUCED_MOTION} {
    &::after {
      animation: none;
    }
  }
`;

export const SideBody = styled.div<{ $center?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 24px 16px;
  align-items: ${({ $center }) => ($center ? 'center' : 'stretch')};
`;

/* Review — centred preference summary rows */
export const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
`;

export const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${CUST.panelDivider};
`;

export const SumLabel = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: ${CUST.textMuted};
  text-align: center;
`;

export const SumValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${CUST.white};
`;

export const SumValue = styled(Text)`
  font-size: 20px;
  line-height: 1.15;
  color: ${CUST.white};
`;

export const FlagImg = styled.img`
  width: 24px;
  height: 18px;
  border-radius: 3px;
  object-fit: cover;
  background: ${CUST.flagRing};
`;

export const SumLogo = styled.img`
  height: 32px;
  max-width: 160px;
  object-fit: contain;
`;

/* Deploying / done — steps checklist (left-aligned) */
export const DoneHeading = styled(Text)`
  align-self: flex-start;
  font-size: 14px;
  color: ${CUST.summaryStrong};
`;

/* Soft caret blink at the end of the live status line (steps(1) = hard on/off, no
   cross-fade, so it reads as a real cursor rather than a pulsing dot). */
const caretBlink = keyframes`
  0%, 45% { opacity: 1; }
  50%, 95% { opacity: 0; }
  100% { opacity: 1; }
`;

/**
 * The live "Lumio is working…" status line that replaces the static "Done so far"
 * heading WHILE DEPLOYING — a rotating typed phrase (see `useTypewriterStatus`)
 * trailed by a blinking caret, so the panel reads as actively working even in the
 * seconds before the first websocket step lands. Same size/colour as DoneHeading;
 * the caret picks up Lumio's magenta. The typed substring is rendered raw (NOT via
 * `<Text>`) — it's a slice of an already-translated phrase, so routing each partial
 * through i18n would mint garbage keys; the full phrases ARE harvested at their
 * `t(...)` call sites. A reserved min-height stops the panel jumping when empty.
 */
export const TypingHeading = styled.span`
  align-self: flex-start;
  /* Hard single line: the typed text must NEVER wrap to a second line (that shifts
     the whole panel as a phrase grows). nowrap forbids the wrap; a too-long phrase
     clips with a true ellipsis instead. max-width bounds it so the ellipsis can
     trigger whether the parent stretches (desktop) or hugs content (mobile). */
  display: block;
  max-width: 100%;
  min-height: 18px;
  font-size: 14px;
  line-height: 1.15;
  color: ${CUST.summaryStrong};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Caret trails the last typed char inline (so it hugs the text as it grows),
     vertically centred against the line. */
  &::after {
    content: '';
    display: inline-block;
    vertical-align: middle;
    width: 2px;
    height: 13px;
    margin-left: 3px;
    border-radius: 1px;
    background: ${CUST.lumioBorder};
    animation: ${caretBlink} ${CUST_ANIM.STATUS_CARET_MS}ms steps(1, end) infinite;
  }

  ${CUST_REDUCED_MOTION} {
    &::after {
      animation: none;
    }
  }
`;

export const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

/* As each deploy step streams in over the websocket it is appended to the list, so
   a freshly-mounted row IS the "step completed" moment — the enter animation rides
   that. Existing rows keep their DOM node (stable keys) and don't re-run on the
   per-tick progress re-render. A small fade + rise; the check ticks a beat later. */
const stepReveal = keyframes`
  from { opacity: 0; transform: translateY(${CUST_ANIM.STEP_REVEAL_SHIFT_PX}px); }
  to { opacity: 1; transform: translateY(0); }
`;

const checkSettle = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to { opacity: 1; transform: scale(1); }
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  animation: ${stepReveal} ${CUST_ANIM.STEP_REVEAL_MS}ms ${CUST_ANIM.SCREEN_EASE} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StepCheck = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  color: ${CUST.deployCheck};
  animation: ${checkSettle} ${CUST_ANIM.STEP_CHECK_MS}ms ${CUST_ANIM.SCREEN_EASE}
    ${CUST_ANIM.STEP_CHECK_DELAY_MS}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StepText = styled(Text)<{ $finished?: boolean }>`
  font-size: 14px;
  line-height: 1.15;
  color: ${({ $finished }) => ($finished ? CUST.deployStepFinished : CUST.deployStepText)};
  opacity: ${({ $finished }) => ($finished ? 0.7 : 1)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

/* Footer CTA — the big green "Finish setup" / "Go to dashboard" button (plus, on
   the done state, a secondary "Preview Website" button stacked above it). */
export const SideFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 24px 24px;
`;

export const Cta = styled.button<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* Larger than a normal step button (48px) to read as the final commit, but not
     the oversized 100px banner it used to be. Uses the standard primary accent. */
  height: 64px;
  border: none;
  border-radius: 16px;
  background: ${({ $enabled }) => ($enabled ? CUST.accent : CUST.ctaDisabledBg)};
  color: ${CUST.ctaText};
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'not-allowed')};
  transition: filter ${CUST_ANIM.INTERACT_MS}ms ease;

  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}

  ${(p) =>
    !p.$enabled &&
    css`
      &:hover::before,
      &:hover::after,
      &:focus-visible::before,
      &:focus-visible::after {
        opacity: 0;
      }
    `}

  &:hover {
    filter: ${({ $enabled }) => ($enabled ? 'brightness(1.05)' : 'none')};
  }
`;

export const CtaLabel = styled(Text)`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  white-space: nowrap;
`;

/* Secondary "Preview Website" button on the done state — a ghost/outline twin of
   `Cta`, stacked above it, that opens the now-live site in a new tab. */
export const PreviewCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 56px;
  border: 1px solid ${CUST.cardBorder};
  border-radius: 16px;
  background: transparent;
  color: ${CUST.white};
  cursor: pointer;
  transition: background ${CUST_ANIM.INTERACT_MS}ms ease;

  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}

  &:hover {
    background: ${CUST.headerAvatarBgHover};
  }
`;

/* ── Mobile ──────────────────────────────────────────────────────────────── */

export const MobileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

export const MHeading = styled(Text)`
  margin-top: 4px;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.1;
  text-align: center;
  background-image: ${CUST_GRADIENTS.headingGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const MCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 16px;
  border: 1px solid ${CUST.cardBorder};
  border-radius: 16px;
  background: ${CUST.cardSurface};
`;

export const MCardLabel = styled(Text)`
  font-size: 12px;
  color: ${CUST.textMuted};
  text-align: center;
`;

export const MCardValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${CUST.white};
`;

export const MCardValue = styled(Text)`
  font-size: 17px;
  font-weight: 600;
  color: ${CUST.white};
`;

export const MProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`;

export const MList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const MFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px 0 8px;
  margin-top: 8px;
`;

export const MPrimary = styled.button<{ $green?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 14px;
  background: ${({ $green }) => ($green ? CUST.deployGreen : CUST.accent)};
  color: ${({ $green }) => ($green ? CUST.deployGreenText : CUST.onAccent)};
  cursor: pointer;

  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
`;

export const MSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  border: 1px solid ${CUST.cardBorder};
  border-radius: 14px;
  background: transparent;
  color: ${CUST.white};
  cursor: pointer;

  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
`;

export const MBtnLabel = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
`;

/* Mobile "your website is live" screen: the iPhone preview fills the body, and the
   two buttons are pinned at the bottom over a fade-out scrim (the phone sits under
   them and fades into the page bg). */
export const MDoneRoot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  /* Fill the step wrapper (so the phone preview stretches + the footer pins to the
     bottom) via flex-grow rather than height:100%. The wrapper is now min-height
     driven (grows with content) so a percentage height would have no definite base
     to resolve against and would collapse to content. */
  flex: 1 1 auto;
  min-height: 0;
`;

export const MDonePreview = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
`;

export const MDoneFooter = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 56px 0 calc(8px + env(safe-area-inset-bottom));
  background: ${CUST_GRADIENTS.mobileFooterScrim};
  pointer-events: none;

  & > button {
    pointer-events: auto;
  }
`;

/* ── "waiting for payment" notice ─────────────────────────────────────────────
 * Shown above the launch CTA when the session's invoice is known to be unpaid — the
 * bank-transfer (SPEI) case, where an owner can reach onboarding while their money is
 * still in flight. Tinted with the customizer accent rather than an error red: nothing
 * has gone wrong, the transfer simply hasn't landed yet. */

export const UnpaidRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  border: 1px solid ${CUST.accentTint};
  border-radius: 12px;
  background: ${CUST.accentTint};
  padding: 12px 14px;
`;

export const UnpaidIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  align-items: center;
  justify-content: center;
  color: ${CUST.accent};
`;

export const UnpaidText = styled(Text)`
  font-size: 13px;
  line-height: 1.4;
  color: ${CUST.textMuted};
`;
